import { ListingType, PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';
import { Grid, ProductColor } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { shallowEqual } from 'react-redux';

import { useZodForm } from '../../hooks/useZodForm.ts';
import {
  selectCompSequenceByListingType,
  selectHasArvOption,
  selectSalesComparisonApproach,
} from '../../redux/features/selectors/orderSelectors.ts';
import { selectPropertyDataCompTypes } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import {
  selectAivArvActiveTab,
  selectPropertyGridExpandedPropertyIds,
} from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { PropertyGridListingTypeProvider } from './PropertyGridListingTypeContext.tsx';
import { PropertyGridColumn } from './PropertyGridColumn.tsx';
import { PropertyGridLabelColumn } from './PropertyGridLabelColumn.tsx';
import {
  AIV_ARV_PROPERTY_GRID_COLUMN_WIDTH,
  LABEL_COLUMN_WIDTH,
  PROPERTY_GRID_COLUMN_WIDTH,
} from './consts.ts';
import {
  ColumnType,
  PropertyGridColumnSectionComponent,
  PropertyGridRowFieldType,
  PropertyGridType,
  schPropertyGridForm,
} from './types.ts';
import { getDataSourcesForGridType, getDefaultFormColumns } from './utils.ts';

type PropertyGridProps = {
  columnSections: PropertyGridColumnSectionComponent[];
  gridType: PropertyGridType;
  labelColumnHeader: ReactNode;
  listingType?: ListingType;
};

export const PropertyGrid = ({
  columnSections,
  gridType,
  labelColumnHeader,
  listingType,
}: PropertyGridProps) => {
  const compSequence = useAppSelector((state) =>
    selectCompSequenceByListingType(state, listingType),
  );
  const propertyGridExpandedPropertyIds = useAppSelector(selectPropertyGridExpandedPropertyIds);
  const expandedPropertyId = propertyGridExpandedPropertyIds?.[gridType];
  const prevCompSequenceRef = useRef<string[]>(compSequence);
  const salesComparisonApproach = useAppSelector(selectSalesComparisonApproach);
  const hasArvOption = useAppSelector(selectHasArvOption);
  const activeTab = useAppSelector(selectAivArvActiveTab);
  const propertyDataCompTypes = useAppSelector(selectPropertyDataCompTypes, shallowEqual);

  const isFeatureGrid = gridType === 'FeatureGrid';
  const isAdjustmentsGrid = gridType === 'AdjustmentsGrid';
  const gridDataSources = getDataSourcesForGridType(gridType);

  const zodForm = useZodForm({
    schema: schPropertyGridForm,
    defaultValues: schPropertyGridForm.parse({
      columns: getDefaultFormColumns(compSequence, gridDataSources),
      dataSources: gridDataSources,
    }),
    mode: 'onBlur',
  });

  const { control, getValues, setValue } = zodForm;

  const columns = useMemo(
    () => getDefaultFormColumns(compSequence, gridDataSources),
    [compSequence, gridDataSources],
  );

  // Update form columns only when new comps are added (not when sequence changes)
  useEffect(() => {
    const prevCompSequence = prevCompSequenceRef.current;
    const newComps = compSequence.filter((compId) => !prevCompSequence.includes(compId));
    const currentColumns = getValues('columns') || [];

    const deletedComps = prevCompSequence.filter((compId) => !compSequence.includes(compId));
    if (deletedComps.length > 0) {
      // Remove columns for deleted comps
      const columnsToRemove = currentColumns.filter((col) =>
        deletedComps.some((deletedCompId) => deletedCompId === col.propertyId),
      );
      setValue(
        'columns',
        currentColumns.filter(
          (col) =>
            !columnsToRemove.some((colToRemove) => colToRemove.propertyId === col.propertyId),
        ),
      );
    }

    // This is a bit messy, but it works
    // Reordering columns seems to move property IDs around but not their underlying data
    // So we need to find the correct property ID for each column and update it
    if (newComps.length === 0 && deletedComps.length === 0) {
      const newCols = [...currentColumns];
      const numberOfColumnsForSubject = currentColumns.filter(
        (col) => col.propertyId === PROPERTY_GRID_SUBJECT_PROPERTY,
      ).length;
      currentColumns.forEach((col, index) => {
        if (!col.propertyData || index < numberOfColumnsForSubject) return;

        const numberOfColumnsPerProperty = currentColumns.filter(
          (otherCol) => otherCol.propertyId === col.propertyId,
        ).length;
        if (col.propertyId !== compSequence[Math.floor(index / numberOfColumnsPerProperty) - 1]) {
          const correctPropertyData =
            currentColumns[
              numberOfColumnsForSubject +
                numberOfColumnsPerProperty * compSequence.indexOf(col.propertyId) +
                (index % numberOfColumnsPerProperty)
            ];

          if (!correctPropertyData) return;
          newCols[index] = {
            ...col,
            propertyId: correctPropertyData.propertyId,
          };
        }
      });

      setValue('columns', newCols);
    }

    prevCompSequenceRef.current = compSequence;
  }, [compSequence, getValues, setValue]);

  const orderedColumns = useMemo(() => {
    const subjectCols = columns.filter(
      ({ propertyId }) => propertyId === PROPERTY_GRID_SUBJECT_PROPERTY,
    );

    const filteredCompSequence =
      (isFeatureGrid || gridType === 'AdjustmentsGrid') && hasArvOption
        ? compSequence.filter((compId) => {
            if (activeTab === 'all') return true;
            const compType = propertyDataCompTypes[compId];
            if (!compType) return false;
            return compType === activeTab;
          })
        : compSequence;

    const compCols = filteredCompSequence.flatMap((compId) => {
      const existingCol = columns.filter(({ propertyId }) => propertyId === compId);
      if (existingCol.length > 0) return existingCol;

      return [
        {
          propertyId: compId,
          type: 'property' as ColumnType,
        },
        ...gridDataSources.map((source) => ({
          propertyId: compId,
          type: source,
        })),
      ];
    });

    return [...subjectCols, ...compCols];
  }, [
    compSequence,
    columns,
    isFeatureGrid,
    gridType,
    gridDataSources,
    hasArvOption,
    activeTab,
    propertyDataCompTypes,
  ]);

  const dataSourceDrawerEnabled =
    isFeatureGrid || (isAdjustmentsGrid && salesComparisonApproach === 'unit_rate');

  const visibleColumns = orderedColumns.filter(
    ({ type, propertyId }) =>
      type === 'property' || (dataSourceDrawerEnabled && expandedPropertyId === propertyId),
  );

  const isDataSourceDrawerOpen =
    dataSourceDrawerEnabled &&
    visibleColumns.some(({ propertyId }) => expandedPropertyId === propertyId);

  let backgroundColor: ProductColor = 'surface6';
  if (activeTab === 'aiv') backgroundColor = 'aivLight';
  else if (activeTab === 'arv') backgroundColor = 'arvLight';

  return (
    <PropertyGridListingTypeProvider value={listingType}>
    <Grid
      backgroundColor={backgroundColor}
      gridTemplateColumns={`${LABEL_COLUMN_WIDTH} ${hasArvOption && activeTab === 'all' ? AIV_ARV_PROPERTY_GRID_COLUMN_WIDTH : PROPERTY_GRID_COLUMN_WIDTH} repeat(${visibleColumns.length}, ${PROPERTY_GRID_COLUMN_WIDTH})`}
      paddingLeft="10px"
      paddingRight="10px"
      paddingBottom="5rem"
      minWidth="max-content"
      width="100%"
    >
      <PropertyGridLabelColumn
        columnSections={columnSections}
        gridType={gridType}
        headerContent={labelColumnHeader}
        control={control}
        getValues={getValues}
        setValue={setValue}
      />

      {orderedColumns.map((col) => {
        const { propertyId, type } = col;
        const isSubjectProperty = propertyId === PROPERTY_GRID_SUBJECT_PROPERTY;

        const columnIndex = columns.findIndex(
          (column) => column.propertyId === propertyId && column.type === type,
        );

        const fieldTypes: PropertyGridRowFieldType[] = [];

        if (isFeatureGrid) {
          if (type === 'property') {
            fieldTypes.push('editableValue');
          } else {
            fieldTypes.push('dataSourceValue');
          }
        } else if (isAdjustmentsGrid) {
          if (type !== 'property') {
            // Data-source column in AdjustmentsGrid (regression / sensitivity)
            if (salesComparisonApproach === 'unit_rate') fieldTypes.push('readOnlyUnitRate');
          } else if (isSubjectProperty) {
            // For subject column in AdjustmentsGrid, filter based on activeTab
            if (hasArvOption) {
              const showAivValue = activeTab === 'all' || activeTab === 'aiv';
              const showArvValue = activeTab === 'all' || activeTab === 'arv';
              if (showAivValue) fieldTypes.push('readOnlyValue');
              if (showArvValue) fieldTypes.push('readOnlyArvValue');
            } else {
              fieldTypes.push('readOnlyValue');
            }

            if (salesComparisonApproach === 'unit_rate') fieldTypes.push('editableUnitRate');
          } else {
            fieldTypes.push('readOnlyValue');
            fieldTypes.push('adjustment');
          }
        }

        if (hasArvOption && isFeatureGrid) fieldTypes.push('editableArvValue');

        return (
          <PropertyGridColumn
            key={`${propertyId}-${type}`}
            fieldTypes={fieldTypes}
            columnSections={columnSections}
            gridType={gridType}
            isDataSourceDrawerOpen={isDataSourceDrawerOpen}
            propertyId={propertyId}
            type={type}
            columnIndex={columnIndex}
            control={control}
            getValues={getValues}
            setValue={setValue}
            listingType={listingType}
          />
        );
      })}
    </Grid>
    </PropertyGridListingTypeProvider>
  );
};
